/*
  7Raw header runtime (docs/screens/01-header.md §6, §9 and A01-*; 00 A00-2; UPDATE-02 "Header on the home page").
  - <r7-header>: transparent → solid, hide on scroll down / show on scroll up, --r7-header-* and
    --header-height, mega menus (hover, focus, touch, keyboard), wide search panel, backdrop,
    Escape through R7.layers, bag bump on add, closing everything when the bag drawer opens.
    Home stage (contract with 7r-stage.js): body[data-stage-tone] is present only while the stage is in
    view and holds the active screen's tone ('dark' | 'light'). While it is present the header stays
    transparent and never auto-hides; the logo grows (.is-big-logo, theme setting r7_big_logo, home page
    only) and, on light screens, the bar turns ink (.is-ink). Neither applies while a mega menu, search,
    the mobile menu or the bag is open. The header publishes html.r7-overlay-open (mega, search, mobile
    menu) and html.r7-bag-open (cart drawer) for the stage's rail and Pause button.
  - <r7-mobile-menu>: full-screen menu below 1080px (open/close, staggered fade, scroll lock,
    focus trap over the header bar + dialog, Escape, focus return).
  - <r7-currency>: submits the localization form when the currency changes.
*/
(() => {
  const WIDE_QUERY = '(min-width: 1080px)';
  const MENU_CLOSE_MS = 300;
  const BUMP_MS = 730;
  const HIDE_DELTA = 24;
  const TOP_ZONE = 100;
  /* 7r-stage.js's in-view rule: the stage's bottom is below 55% of the viewport and its top above 45%. */
  const STAGE_BOTTOM_RATIO = 0.55;
  const STAGE_TOP_RATIO = 0.45;
  const ADD_SOURCES = ['product-form', 'r7', 'quick-add'];
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])';

  const reducedMotion = () =>
    window.R7
      ? window.R7.prefersReducedMotion()
      : document.documentElement.classList.contains('r7-rm') &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isShown = (el) => {
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ visibilityProperty: true, opacityProperty: false });
    }
    return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  };

  const focusablesIn = (root) => Array.from(root.querySelectorAll(FOCUSABLE)).filter(isShown);

  const layerPush = (id, close) => window.R7 && window.R7.layers.push(id, close);
  const layerRemove = (id) => window.R7 && window.R7.layers.remove(id);

  /* r7-search (snippets/7r-search-panel.liquid) exposes clear(), which also restores the menu groups. */
  const clearQuery = (searchEl) => {
    if (searchEl && typeof searchEl.clear === 'function') searchEl.clear();
  };

  if (!customElements.get('r7-mobile-menu')) {
    class R7MobileMenu extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('.r7-mmenu');
        this.scope = this.closest('r7-header') || this;
        this.toggleButton = this.scope.querySelector(`[aria-controls="${this.dialog.id}"]`);
        this.search = this.dialog.querySelector('r7-search');
        this.isOpen = false;
        this.isIn = false;

        this.onToggleClick = (event) => this.toggle(event.detail === 0);
        this.onKeydown = this.onKeydown.bind(this);

        this.toggleButton?.addEventListener('click', this.onToggleClick);
      }

      disconnectedCallback() {
        this.toggleButton?.removeEventListener('click', this.onToggleClick);
        document.removeEventListener('keydown', this.onKeydown);
        clearTimeout(this.closeTimer);
        layerRemove('r7-menu');
        if (this.isOpen) document.body.style.overflow = '';
      }

      toggle(fromKeyboard = false) {
        if (this.isIn) this.close({ restoreFocus: fromKeyboard });
        else this.open(fromKeyboard);
      }

      open(fromKeyboard = false) {
        clearTimeout(this.closeTimer);
        if (this.isIn) return;
        const wasOpen = this.isOpen;
        this.isOpen = true;
        this.isIn = true;
        this.dialog.hidden = false;
        this.scope.classList.add('is-menu-open');
        this.setButtonState(true);
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeydown);
        layerPush('r7-menu', () => this.close({ restoreFocus: true }));

        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (this.isIn) this.dialog.classList.add('is-in');
          })
        );

        const input = this.dialog.querySelector('[data-r7-input]');
        if (fromKeyboard && input) input.focus();
        else this.dialog.focus({ preventScroll: true });

        if (!wasOpen) this.emit(true);
      }

      close({ restoreFocus = false, instant = false } = {}) {
        if (!this.isOpen) return;
        clearTimeout(this.closeTimer);
        this.isIn = false;
        this.dialog.classList.remove('is-in');
        document.removeEventListener('keydown', this.onKeydown);
        layerRemove('r7-menu');
        if (restoreFocus && this.toggleButton) this.toggleButton.focus();

        if (instant || reducedMotion()) this.unmount();
        else this.closeTimer = setTimeout(() => this.unmount(), MENU_CLOSE_MS);
      }

      unmount() {
        this.isOpen = false;
        this.isIn = false;
        this.dialog.classList.remove('is-in');
        this.dialog.hidden = true;
        this.scope.classList.remove('is-menu-open');
        this.setButtonState(false);
        document.body.style.overflow = '';
        clearQuery(this.search);
        this.emit(false);
      }

      setButtonState(open) {
        if (!this.toggleButton) return;
        this.toggleButton.setAttribute('aria-expanded', String(open));
        const label = open ? this.toggleButton.dataset.labelClose : this.toggleButton.dataset.labelOpen;
        if (label) this.toggleButton.setAttribute('aria-label', label);
      }

      emit(open) {
        this.dispatchEvent(new CustomEvent('r7:menu-change', { bubbles: true, detail: { open } }));
      }

      /* Focus stays within the header bar (hamburger, logo, Bag) and the dialog (01 A01-9). */
      onKeydown(event) {
        if (event.key !== 'Tab' || !this.isIn) return;
        const items = focusablesIn(this.scope);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        const inside = this.scope.contains(active);

        if (event.shiftKey && (!inside || active === first || active === this.dialog)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (!inside || active === last)) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    customElements.define('r7-mobile-menu', R7MobileMenu);
  }

  if (!customElements.get('r7-currency')) {
    class R7Currency extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.select = this.querySelector('select');
        this.onChange = () => this.form.submit();
        this.select?.addEventListener('change', this.onChange);
      }

      disconnectedCallback() {
        this.select?.removeEventListener('change', this.onChange);
      }
    }

    customElements.define('r7-currency', R7Currency);
  }

  if (!customElements.get('r7-header')) {
    class R7Header extends HTMLElement {
      connectedCallback() {
        this.wrapper = this.closest('.shopify-section') || this;
        this.transparentEnabled = this.dataset.transparent === 'true';
        this.hideEnabled = this.dataset.hideOnScroll === 'true';
        this.bigLogoEnabled = this.dataset.bigLogo === 'true';
        this.wide = window.matchMedia(WIDE_QUERY);

        this.mega = null;
        this.searchOpen = false;
        this.menuOpen = false;
        this.bagOpen = false;
        this.scrolled = false;
        this.scrollHidden = false;
        this.stageIn = false;
        this.stageTone = null;
        this.stageReported = false;
        this.anchor = null;
        this.lastPointer = 'mouse';
        this.touchWasOpen = false;
        this.suppressFocusOpen = false;

        /* The first frame adopts the real logo state without animating (restored scroll, editor re-render). */
        this.classList.add('is-booting');
        this.bootFrame = requestAnimationFrame(() => {
          this.bootFrame = requestAnimationFrame(() => this.classList.remove('is-booting'));
        });

        this.panel = this.querySelector('[data-r7-mega]');
        this.triggers = Array.from(this.querySelectorAll('[data-mega-key]'));
        this.navLinks = Array.from(this.querySelectorAll('.r7-nav__link'));
        this.logo = this.querySelector('.r7-hdr__logo');
        this.backdrop = this.querySelector('[data-r7-backdrop]');
        this.searchButton = this.querySelector('[data-r7-search-open]');
        this.search = this.querySelector('.r7-hdr r7-search[data-context="wide"]');
        this.menu = this.querySelector('r7-mobile-menu');
        this.bag = this.querySelector('#cart-icon-bubble');
        this.bagLive = this.querySelector('[data-r7-bag-live]');

        if (this.search && this.search.id) this.searchButton?.setAttribute('aria-controls', this.search.id);

        this.bindStage();
        this.bindScroll();
        this.bindMega();
        this.bindSearch();
        this.bindMenu();
        this.bindBag();
        this.bindDrawer();
        this.bindEditor();

        this.onBreakpoint = () => this.closeAll({ instant: true });
        this.wide.addEventListener('change', this.onBreakpoint);

        this.resizeObserver = new ResizeObserver(() => this.setHeaderHeight());
        this.resizeObserver.observe(this.wrapper);
        this.setHeaderHeight();

        this.onScroll();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScrollPassive);
        this.wide.removeEventListener('change', this.onBreakpoint);
        this.resizeObserver?.disconnect();
        this.bagObserver?.disconnect();
        this.drawerObserver?.disconnect();
        this.stageObserver?.disconnect();
        this.unsubCart?.();
        document.removeEventListener('r7:stage-change', this.onStageChange);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        clearTimeout(this.bumpTimer);
        cancelAnimationFrame(this.scrollFrame);
        cancelAnimationFrame(this.bootFrame);
        layerRemove('r7-mega');
        layerRemove('r7-search');
        document.documentElement.classList.remove('r7-header-hidden', 'r7-overlay-open', 'r7-bag-open');
      }

      /* ---------- State → DOM ---------- */

      get overlayOpen() {
        return Boolean(this.mega) || this.searchOpen || this.menuOpen;
      }

      /*
        Prototype v7 hdr (L1169-1171, L1207): over the stage the header is transparent and never auto-hides;
        the big logo and the ink are off while any header layer or the bag is open. Off the stage, and on
        every page without one, the behaviour is the one before Update 02.
      */
      render() {
        const root = document.documentElement;
        const overlay = this.overlayOpen;
        const quiet = !overlay && !this.bagOpen;
        const transparent = this.transparentEnabled && (this.stageIn || !this.scrolled) && !overlay;
        this.classList.toggle('is-transparent', transparent);
        this.classList.toggle('is-big-logo', this.bigLogoEnabled && this.stageIn && quiet);
        this.classList.toggle('is-ink', this.stageIn && this.stageTone === 'light' && quiet);
        /* The Liquid first-paint state is replaced by the real one in the same frame (no shrink and regrow). */
        this.classList.remove('is-big-logo-boot');
        this.backdrop?.classList.toggle('is-open', Boolean(this.mega) || this.searchOpen);
        const hide = this.hideEnabled && this.scrollHidden && !this.stageIn && quiet;
        root.classList.toggle('r7-header-hidden', hide);
        root.classList.toggle('r7-overlay-open', overlay);
        root.classList.toggle('r7-bag-open', this.bagOpen);
      }

      setHeaderHeight() {
        document.documentElement.style.setProperty('--header-height', `${this.wrapper.offsetHeight}px`);
      }

      closeAll({ instant = false } = {}) {
        this.closeMega();
        this.closeSearch({ clear: true });
        this.menu?.close({ instant });
      }

      /* ---------- Scroll (01 §6.3) ---------- */

      bindScroll() {
        this.onScrollPassive = () => {
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => this.onScroll());
        };
        window.addEventListener('scroll', this.onScrollPassive, { passive: true });
      }

      onScroll() {
        const y = window.scrollY;
        const scrolled = y > Math.min(420, window.innerHeight * 0.4);
        const anchor = this.anchor ?? y;
        let hidden = this.scrollHidden;

        if (y < TOP_ZONE) {
          hidden = false;
          this.anchor = y;
        } else if (y - anchor > HIDE_DELTA) {
          hidden = true;
          this.anchor = y;
        } else if (anchor - y > HIDE_DELTA) {
          hidden = false;
          this.anchor = y;
        } else if ((hidden && y > anchor) || (!hidden && y < anchor)) {
          this.anchor = y;
        }

        this.scrolled = scrolled;
        this.scrollHidden = hidden;
        if (!this.stageReported) this.syncStage();
        this.render();
      }

      /* ---------- Home stage (UPDATE-02; contract with 7r-stage.js) ---------- */

      /* Script order does not matter: the attribute is read now and observed from here on. */
      bindStage() {
        this.stageObserver = new MutationObserver(() => {
          this.syncStage();
          this.render();
        });
        this.stageObserver.observe(document.body, { attributes: true, attributeFilter: ['data-stage-tone'] });
        this.onStageChange = () => {
          this.stageReported = true;
          this.syncStage();
          this.render();
        };
        document.addEventListener('r7:stage-change', this.onStageChange);
        /* This script is deferred, so the page is parsed: no stage now means nothing to wait for. */
        this.stageEl = document.querySelector('.r7-stage');
        this.stageReported = !this.stageEl;
        this.syncStage();
      }

      /*
        Present attribute = stage in view, value = active screen tone. Until the stage has reported (the
        attribute appears or r7:stage-change fires), the header applies the stage's own two-sided rule,
        so the big logo painted by Liquid is kept while 7r-stage.js loads instead of shrinking and
        regrowing, and a stage further down the page does not count as in view before it arrives.
      */
      syncStage() {
        const tone = document.body.dataset.stageTone;
        if (tone !== undefined || !this.stageEl?.isConnected) this.stageReported = true;
        if (this.stageReported) {
          this.stageIn = tone !== undefined;
          this.stageTone = this.stageIn ? tone : null;
          return;
        }
        const rect = this.stageEl.getBoundingClientRect();
        const viewport = window.innerHeight;
        this.stageIn = rect.bottom > viewport * STAGE_BOTTOM_RATIO && rect.top < viewport * STAGE_TOP_RATIO;
        this.stageTone = null;
      }

      /* ---------- Mega menus (01 §6.4, §9) ---------- */

      bindMega() {
        if (!this.panel) return;

        this.addEventListener('pointerdown', (event) => {
          this.lastPointer = event.pointerType;
        });

        /* The <header> holds the bar and the panel but not the backdrop, as in the prototype (L39, L174). */
        this.querySelector('.r7-hdr').addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse') this.closeMega();
        });

        this.triggers.forEach((trigger) => {
          const key = trigger.dataset.megaKey;
          trigger.addEventListener('pointerenter', (event) => {
            if (event.pointerType === 'mouse') this.openMega(key);
          });
          trigger.addEventListener('pointerdown', (event) => {
            this.touchWasOpen = this.mega === key;
            this.lastPointer = event.pointerType;
          });
          trigger.addEventListener('focus', () => {
            if (this.suppressFocusOpen || !this.wide.matches) return;
            this.openMega(key);
          });
          trigger.addEventListener('click', (event) => {
            /* Keyboard clicks (Enter: detail 0) always follow the link, whatever the last pointer was. */
            if (event.detail === 0 || this.lastPointer !== 'touch' || !this.wide.matches || this.touchWasOpen) return;
            event.preventDefault();
            this.openMega(key);
            this.touchWasOpen = true;
          });
          trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event, trigger, key));
        });

        this.querySelectorAll('[data-mega-close]').forEach((el) => {
          el.addEventListener('pointerenter', (event) => {
            if (event.pointerType === 'mouse') this.closeMega();
          });
          el.addEventListener('focus', () => this.closeMega());
        });

        this.addEventListener('focusout', (event) => {
          if (this.mega && event.relatedTarget && !this.contains(event.relatedTarget)) this.closeMega();
        });

        this.panel.querySelectorAll('[data-mega-content]').forEach((content) => {
          content.querySelectorAll('[data-mega-row]').forEach((row) => {
            const activate = () => this.setActiveRow(content, row.dataset.megaRow);
            row.addEventListener('pointerenter', activate);
            row.addEventListener('focus', activate);
          });
          content.addEventListener('keydown', (event) => this.onPanelKeydown(event, content));
        });

      }

      triggerFor(key) {
        return this.triggers.find((trigger) => trigger.dataset.megaKey === key);
      }

      contentFor(key) {
        return this.panel.querySelector(`[data-mega-content="${CSS.escape(key)}"]`);
      }

      openMega(key) {
        if (!this.panel || !this.wide.matches || this.mega === key) return;
        const content = this.contentFor(key);
        if (!content) return;

        if (this.searchOpen) this.closeSearch({ clear: false });
        if (!this.panel.classList.contains('is-primed')) {
          this.panel.classList.add('is-primed');
          void this.panel.offsetWidth;
        }

        this.panel.querySelectorAll('[data-mega-content]').forEach((el) => {
          el.hidden = el !== content;
        });
        this.resetRows(content);
        this.mega = key;
        this.panel.classList.add('is-open');
        this.triggers.forEach((trigger) => {
          const open = trigger.dataset.megaKey === key;
          trigger.setAttribute('aria-expanded', String(open));
          trigger.classList.toggle('is-open', open);
        });
        layerPush('r7-mega', () => this.closeMega({ focusTrigger: true }));
        this.render();
      }

      closeMega({ focusTrigger = false } = {}) {
        if (!this.mega) return;
        const trigger = this.triggerFor(this.mega);
        this.mega = null;
        this.panel.classList.remove('is-open');
        this.triggers.forEach((el) => {
          el.setAttribute('aria-expanded', 'false');
          el.classList.remove('is-open');
        });
        layerRemove('r7-mega');
        if (focusTrigger && trigger && this.contains(document.activeElement)) this.focusWithoutOpening(trigger);
        this.render();
      }

      focusWithoutOpening(el) {
        this.suppressFocusOpen = true;
        el.focus();
        this.suppressFocusOpen = false;
      }

      resetRows(content) {
        const list = content.querySelector('[data-mega-list]');
        list?.classList.remove('is-dimmed');
        this.showRow(content, '0');
      }

      setActiveRow(content, index) {
        content.querySelector('[data-mega-list]')?.classList.add('is-dimmed');
        this.showRow(content, index);
      }

      showRow(content, index) {
        content.querySelectorAll('[data-mega-row]').forEach((row) => {
          row.classList.toggle('is-active', row.dataset.megaRow === index);
        });
        content.querySelectorAll('[data-mega-img]').forEach((img) => {
          img.classList.toggle('is-active', img.dataset.megaImg === index);
        });
        content.querySelectorAll('[data-mega-caption]').forEach((caption) => {
          caption.hidden = caption.dataset.megaCaption !== index;
        });
      }

      onTriggerKeydown(event, trigger, key) {
        if (!this.wide.matches) return;
        const content = this.contentFor(key);
        if (!content) return;

        if ((event.key === 'Tab' && !event.shiftKey && this.mega === key) || event.key === 'ArrowDown') {
          if (this.mega !== key) this.openMega(key);
          const first = focusablesIn(content)[0];
          if (!first) return;
          event.preventDefault();
          first.focus();
        }
      }

      onPanelKeydown(event, content) {
        const items = focusablesIn(content);
        const index = items.indexOf(document.activeElement);
        if (index === -1) return;
        const trigger = this.triggerFor(content.dataset.megaContent);

        if (event.key === 'Tab' && event.shiftKey && index === 0) {
          event.preventDefault();
          this.focusWithoutOpening(trigger);
        } else if (event.key === 'Tab' && !event.shiftKey && index === items.length - 1) {
          event.preventDefault();
          const position = this.navLinks.indexOf(trigger);
          const next = this.navLinks[position + 1] || this.logo;
          this.closeMega();
          next.focus();
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          const rows = Array.from(content.querySelectorAll('[data-mega-row]'));
          const rowIndex = rows.indexOf(document.activeElement);
          if (rowIndex === -1) return;
          event.preventDefault();
          const step = event.key === 'ArrowDown' ? 1 : -1;
          const target = rows[rowIndex + step];
          if (target) target.focus();
          else if (step === -1) this.focusWithoutOpening(trigger);
        }
      }

      /* ---------- Wide search panel (01 §6.5; r7-search contract from T3) ---------- */

      bindSearch() {
        this.backdrop?.addEventListener('click', () => {
          this.closeMega();
          this.closeSearch({ clear: true });
        });

        this.searchButton?.addEventListener('click', () => {
          if (this.searchOpen) this.closeSearch({ clear: true, restoreFocus: true });
          else this.openSearch();
        });

        this.addEventListener('r7:search-close', (event) => {
          const context = event.target.closest('r7-search')?.dataset.context;
          if (context === 'menu') this.menu?.close({ restoreFocus: true });
          else this.closeSearch({ clear: true, restoreFocus: true });
        });
      }

      openSearch() {
        if (!this.search || !this.wide.matches || this.searchOpen) return;
        this.closeMega();
        this.searchOpen = true;
        this.search.setAttribute('open', '');
        this.searchButton?.setAttribute('aria-expanded', 'true');
        layerPush('r7-search', () => this.closeSearch({ clear: true, restoreFocus: true }));
        if (typeof this.search.focusInput === 'function') this.search.focusInput();
        this.render();
      }

      closeSearch({ clear = true, restoreFocus = false } = {}) {
        if (!this.searchOpen) return;
        this.searchOpen = false;
        const hadFocus = this.search.contains(document.activeElement) || document.activeElement === document.body;
        this.search.removeAttribute('open');
        this.searchButton?.setAttribute('aria-expanded', 'false');
        layerRemove('r7-search');
        if (clear) clearQuery(this.search);
        if (restoreFocus && hadFocus) this.searchButton?.focus();
        this.render();
      }

      /* ---------- Mobile menu ---------- */

      bindMenu() {
        this.addEventListener('r7:menu-change', (event) => {
          this.menuOpen = event.detail.open;
          if (this.menuOpen) {
            this.closeMega();
            this.closeSearch({ clear: false });
          }
          this.render();
        });
      }

      /* ---------- Bag count and bump (00 A00-2, A00-3) ---------- */

      bindBag() {
        if (!this.bag) return;
        const read = () => Number(this.bag.querySelector('[data-count]')?.dataset.count || 0);
        let last = read();
        let pendingAdd = false;

        /* An add opens the drawer: close every header layer first, so only the drawer traps focus and Esc. */
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (!event || !ADD_SOURCES.includes(event.source)) return;
            pendingAdd = true;
            this.closeAll({ instant: true });
          });
        }
        this.bag.addEventListener('click', () => this.closeAll({ instant: true }), { capture: true });

        this.bagObserver = new MutationObserver(() => {
          const count = read();
          if (pendingAdd && count > last) {
            if (!reducedMotion()) {
              this.bag.classList.remove('r7-bag--bump');
              void this.bag.offsetWidth;
              this.bag.classList.add('r7-bag--bump');
              clearTimeout(this.bumpTimer);
              this.bumpTimer = setTimeout(() => this.bag.classList.remove('r7-bag--bump'), BUMP_MS);
            }
            if (this.bagLive) this.bagLive.textContent = this.bag.querySelector('.visually-hidden')?.textContent.trim() || '';
          }
          pendingAdd = false;
          last = count;
        });
        this.bagObserver.observe(this.bag, { childList: true });

        /*
          cart-drawer.js binds the bag once, in the CartDrawer constructor, and loads after this file. Decide
          only once it is defined (every check below runs after whenDefined): on page load the constructor has
          already bound the bag (aria-haspopup is set) and nothing is added, so one click opens the drawer once;
          after an editor re-render of this section the new bag is unbound and is bound here (00 §3).
          No cart drawer (cart_type 'page'): cart-drawer.js never loads and the bag stays a plain link.
        */
        const bag = this.bag;
        customElements.whenDefined('cart-drawer').then(() => {
          const drawer = document.querySelector('cart-drawer');
          if (!drawer || typeof drawer.open !== 'function') return;
          if (!bag.isConnected || bag.hasAttribute('aria-haspopup') || bag.dataset.r7DrawerBound) return;
          bag.dataset.r7DrawerBound = 'true';
          bag.setAttribute('role', 'button');
          bag.setAttribute('aria-haspopup', 'dialog');
          bag.addEventListener('click', (event) => {
            event.preventDefault();
            drawer.open(bag);
          });
          bag.addEventListener('keydown', (event) => {
            if (event.code.toUpperCase() !== 'SPACE') return;
            event.preventDefault();
            drawer.open(bag);
          });
        });
      }

      bindDrawer() {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;
        const sync = () => {
          const open = drawer.classList.contains('active');
          if (open === this.bagOpen) return;
          this.bagOpen = open;
          if (open) this.closeAll({ instant: true });
          this.render();
        };
        this.drawerObserver = new MutationObserver(sync);
        this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        sync();
      }

      /* ---------- Theme editor ---------- */

      bindEditor() {
        this.onBlockSelect = (event) => {
          if (!this.contains(event.target)) return;
          const key =
            event.target.dataset.megaKey || event.target.closest('[data-mega-content]')?.dataset.megaContent;
          if (key) this.openMega(key);
        };
        this.onBlockDeselect = (event) => {
          if (this.contains(event.target)) this.closeMega();
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    customElements.define('r7-header', R7Header);
  }
})();
